import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE, KEYWORDS } from './config';

/**
 * Per-page SEO component. Renders title, meta description, OG/Twitter cards,
 * canonical URL, and any JSON-LD schema objects passed in via `schemas`.
 *
 * Pass `title` without the brand suffix — we append " | Brick Lane" automatically.
 */
const SEO = ({
  title,
  description,
  path = '/',
  image = SITE.image,
  type = 'website',
  keywords,
  schemas = [],
  noIndex = false,
  extraTags = [],
}) => {
  const fullTitle = title ? `${title} | ${SITE.name}` : `${SITE.name} \u2014 ${SITE.tagline}`;
  const desc = description || SITE.description;
  const url = `${SITE.url}${path}`;
  // OG/Twitter crawlers require absolute image URLs.
  const absImage = image?.startsWith('http') ? image : `${SITE.url}${image}`;
  const kw = (keywords && keywords.length ? keywords : KEYWORDS).join(', ');

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="author" content={SITE.name} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absImage} />
      <meta property="og:image:alt" content={SITE.name} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absImage} />

      {/* Geo hints help local pack indexing */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="Los Angeles, Arts District" />
      <meta name="geo.position" content={`${SITE.geo.latitude};${SITE.geo.longitude}`} />
      <meta name="ICBM" content={`${SITE.geo.latitude}, ${SITE.geo.longitude}`} />

      {/* JSON-LD structured data */}
      {schemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      {/* Free-form extra meta tags (e.g. Facebook domain verification) */}
      {extraTags.map((tag, idx) => (
        <meta key={`extra-${idx}`} {...tag} />
      ))}
    </Helmet>
  );
};

export default SEO;
